import { useState, useEffect } from 'react';
import { Star, Edit, Trash2, Plus } from 'lucide-react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Select, MenuItem, InputLabel, FormControl,
  Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton
} from '@mui/material';
import httpClient from "../Api/axios";


export default function ReviewManagementSystem() {
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentReview, setCurrentReview] = useState(null);
  const [formData, setFormData] = useState({ star_count: 5, status: "published", name: "", review: "" });

  const fetchReviews = async () => {
    try {
      const response = await httpClient.get('/reviews');
      console.log('Full response:', response.data); 
      setReviews(response.data);
     
    } catch (err) {
      console.log('Fetch error:', err); 
      setReviews([]);
    }
  };
  

  useEffect(() => {
    fetchReviews();
  }, []);

  const addReview = async () => {
    try {
      const response = await httpClient.post('/reviews', formData);
      setReviews([...reviews, response.data]);
      console.log('Add response:', response.data);
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      setError(err.message);
      const newReview = { id: reviews.length + 1, ...formData };
      setReviews([...reviews, newReview]);
      setShowAddModal(false);
      resetForm();
    }
  };

  
  const updateReview = async () => {
    if (!currentReview || !currentReview._id) {
      console.error('No review selected or invalid review ID');
      return;
    }
  
    try {
      const reviewId = currentReview._id;
      const response = await httpClient.put(`/reviews/${reviewId}`, formData);
      setReviews(reviews.map(r => r._id === currentReview._id ? response.data : r));
      setShowEditModal(false);
    } catch (err) {
      setError(err.message);
      setShowEditModal(false);
    }
  };
  
  
  useEffect(() => {
    console.log("Reviews updated:", reviews);
  }, [reviews]);
  
  

  const deleteReview = async () => {
    if (!currentReview) return;

    try {
      await httpClient.delete(`/reviews/${currentReview._id}`);
setReviews(reviews.filter(r => r._id !== currentReview._id));

      setShowDeleteModal(false);
      console.log('Delete response:', currentReview.id);
    } catch (err) {
      setError(err.message);
      setReviews(reviews.filter(r => r.id !== currentReview.id));
      setShowDeleteModal(false);
    }
  };

 


  const handleEdit = (review) => {
    console.log('Selected review for editing:', review);  // Debugging line
    if (!review.id) {
      console.error('Review does not have a valid ID', review);  // Debugging line
    }
    setCurrentReview(review);
    setFormData({
      star_count: review.star_count,
      status: review.status,
      name: review.name,
      review: review.review
    });
    setShowEditModal(true);
  };
  
  

  const handleDelete = (review) => {
    setCurrentReview(review);
    setShowDeleteModal(true);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({ star_count: 5, name: "", status: "published", review: "" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const StarRating = ({ star_count }) => (
    <div className="flex">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={16} fill={i < star_count ? "gold" : "none"} stroke={i < star_count ? "gold" : "gray"} />
      ))}
    </div>
  );

  return (
    <div className="container mx-auto p-6 rounded-lg shadow-lg bg-white">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Review Master</h1>
        <Button variant="contained" color="primary" onClick={handleAdd} startIcon={<Plus size={16} />}>
          Add Review
        </Button>
      </div>

      {error && (
        <Snackbar open={true} autoHideDuration={6000} message={error} />
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>S. No</TableCell>
              <TableCell>Star Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Review</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No reviews found</TableCell>
              </TableRow>
            ) : (
              reviews.map((review, index) => (
                <TableRow key={review._id}>
                <TableCell>{index + 1}</TableCell>
                  <TableCell><StarRating star_count={review.star_count} /></TableCell>
                  <TableCell>{review.status}</TableCell>
                  <TableCell>{review.name}</TableCell>
                  <TableCell>{review.review}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(review)}><Edit /></IconButton>
                    <IconButton onClick={() => handleDelete(review)}><Trash2 /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Modal */}
      <Dialog open={showAddModal} onClose={() => setShowAddModal(false)}>
        <DialogTitle>Add New Review</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Star Count</InputLabel>
            <Select
              value={formData.star_count}
              onChange={handleChange}
              label="Star Count"
              name="star_count"
            >
              {[1, 2, 3, 4, 5].map(count => (
                <MenuItem key={count} value={count}>{count} Star</MenuItem>
              ))}
            </Select>
          </FormControl>
         
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={handleChange}
              label="Status"
              name="status"
            >
              
              <MenuItem value="Good">Good</MenuItem>
              <MenuItem value="Better">Better</MenuItem>
              <MenuItem value="Very Good">Very Good</MenuItem>
              <MenuItem value="Excellent">Excellent</MenuItem>
            </Select>
          </FormControl>

          <TextField
  label="Name"
  name="name"
  value={formData.name}
  onChange={handleChange}
  fullWidth
  margin="normal"
/>
          <TextField
            label="Review"
            name="review"
            value={formData.review}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddModal(false)} color="secondary">Cancel</Button>
          <Button onClick={addReview} color="primary" disabled={!formData.name || !formData.review}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)}>
        <DialogTitle>Edit Review</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Star Count</InputLabel>
            <Select
              value={formData.star_count}
              onChange={handleChange}
              label="Star Count"
              name="star_count"
            >
              {[1, 2, 3, 4, 5].map(count => (
                <MenuItem key={count} value={count}>{count} Star</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={handleChange}
              label="Status"
              name="status"
            >
              <MenuItem value="Good">Good</MenuItem>
              <MenuItem value="Better">Better</MenuItem>
              <MenuItem value="Very Good">Very Good</MenuItem>
              <MenuItem value="Excellent">Excellent</MenuItem>
            </Select>
          </FormControl>

          <TextField
  label="Name"
  name="name"
  value={formData.name}
  onChange={handleChange}
  fullWidth
  margin="normal"
/>
          <TextField
            label="Review"
            name="review"
            value={formData.review}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} color="secondary">Cancel</Button>
          <Button onClick={updateReview} color="primary" disabled={!formData.name || !formData.review}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Delete Review</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this review by <strong>{currentReview?.name}</strong>?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)} color="secondary">Cancel</Button>
          <Button onClick={deleteReview} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
